import React, { useState } from "react";

// ── StatCard ──────────────────────────────────────────────────────────────────
export function StatCard({ label, value, color = "#2563EB", icon }) {
  return (
    <div style={{
      background: "rgba(15,23,42,0.8)",
      border: `1px solid ${color}22`,
      borderRadius: "14px",
      padding: "20px 22px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      flex: "1 1 140px",
      minWidth: "120px",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: "13px", color: "#64748B", fontWeight: "500" }}>{label}</span>
        {icon && <span style={{ fontSize: "18px", opacity: 0.7 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: "32px", fontWeight: "700", color, letterSpacing: "-1px", lineHeight: 1 }}>
        {value ?? <span style={{ fontSize: "20px", opacity: 0.3 }}>—</span>}
      </div>
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
const BADGE_COLORS = {
  active:    { bg: "rgba(34,197,94,0.12)",  text: "#22C55E", dot: "#22C55E" },
  trial:     { bg: "rgba(234,179,8,0.12)",  text: "#EAB308", dot: "#EAB308" },
  suspended: { bg: "rgba(239,68,68,0.12)",  text: "#EF4444", dot: "#EF4444" },
  starter:   { bg: "rgba(100,116,139,0.15)", text: "#94A3B8", dot: "#64748B" },
  growth:    { bg: "rgba(37,99,235,0.12)",  text: "#60A5FA", dot: "#2563EB" },
  enterprise:{ bg: "rgba(124,58,237,0.12)", text: "#A78BFA", dot: "#7C3AED" },
  super_admin:    { bg: "rgba(124,58,237,0.12)", text: "#A78BFA", dot: "#7C3AED" },
  facility_admin: { bg: "rgba(37,99,235,0.12)",  text: "#60A5FA", dot: "#2563EB" },
  staff:          { bg: "rgba(34,197,94,0.12)",  text: "#22C55E", dot: "#22C55E" },
  inactive:  { bg: "rgba(100,116,139,0.15)", text: "#64748B", dot: "#475569" },
};

export function Badge({ value }) {
  const key    = value?.toLowerCase().replace(" ", "_") || "inactive";
  const colors = BADGE_COLORS[key] || BADGE_COLORS.inactive;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      background: colors.bg,
      color: colors.text,
      borderRadius: "6px",
      padding: "3px 8px",
      fontSize: "11px",
      fontWeight: "600",
      letterSpacing: "0.04em",
      textTransform: "capitalize",
      whiteSpace: "nowrap",
    }}>
      <span style={{
        width: "5px", height: "5px", borderRadius: "50%",
        background: colors.dot, flexShrink: 0,
      }} />
      {value}
    </span>
  );
}

// ── FormField ─────────────────────────────────────────────────────────────────
export function FormField({ label, required, hint, error, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      {label && (
        <label style={{
          fontSize: "12px", fontWeight: "600", color: "#94A3B8",
          letterSpacing: "0.06em", textTransform: "uppercase",
        }}>
          {label}{required && <span style={{ color: "#EF4444", marginLeft: "3px" }}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && (
        <span style={{ fontSize: "11px", color: "#475569" }}>{hint}</span>
      )}
      {error && (
        <span style={{ fontSize: "11px", color: "#EF4444" }}>{error}</span>
      )}
    </div>
  );
}

export const inputStyle = {
  background: "rgba(30,41,59,0.8)",
  border: "1px solid rgba(51,65,85,0.8)",
  borderRadius: "8px",
  padding: "9px 12px",
  color: "#F1F5F9",
  fontSize: "14px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

export const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748B' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
  paddingRight: "28px",
};

// ── Btn ───────────────────────────────────────────────────────────────────────
const BTN_VARIANTS = {
  primary:  { bg: "linear-gradient(135deg,#2563EB,#1D4ED8)", color: "#fff", border: "none" },
  danger:   { bg: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#fff", border: "none" },
  ghost:    { bg: "rgba(51,65,85,0.4)", color: "#94A3B8", border: "1px solid rgba(51,65,85,0.6)" },
  success:  { bg: "linear-gradient(135deg,#16A34A,#15803D)", color: "#fff", border: "none" },
  warning:  { bg: "linear-gradient(135deg,#D97706,#B45309)", color: "#fff", border: "none" },
};

export function Btn({ children, variant = "primary", onClick, disabled, size = "md", style: extra = {} }) {
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.primary;
  const pad = size === "sm" ? "6px 12px" : size === "lg" ? "12px 24px" : "8px 16px";
  const fs  = size === "sm" ? "12px" : size === "lg" ? "15px" : "13px";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: v.bg, color: v.color, border: v.border,
        borderRadius: "8px", padding: pad, fontSize: fs,
        fontWeight: "600", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontFamily: "inherit",
        transition: "opacity 0.15s, transform 0.1s",
        whiteSpace: "nowrap",
        letterSpacing: "0.01em",
        ...extra,
      }}
    >
      {children}
    </button>
  );
}

// ── ConfirmModal ──────────────────────────────────────────────────────────────
import Modal from "./Modal";

export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel = "Confirm", variant = "danger", loading }) {
  return (
    <Modal open={open} onClose={onClose} title={title} width="400px">
      <p style={{ color: "#94A3B8", fontSize: "14px", lineHeight: "1.6", marginBottom: "24px" }}>
        {message}
      </p>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose} disabled={loading}>Cancel</Btn>
        <Btn variant={variant} onClick={onConfirm} disabled={loading}>
          {loading ? "Please wait…" : confirmLabel}
        </Btn>
      </div>
    </Modal>
  );
}

// ── PageHeader ────────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start",
      justifyContent: "space-between", marginBottom: "28px", gap: "16px",
      flexWrap: "wrap",
    }}>
      <div>
        <h1 style={{
          fontSize: "26px", fontWeight: "700", color: "#fff",
          margin: "0 0 4px", letterSpacing: "-0.5px",
        }}>{title}</h1>
        {subtitle && (
          <p style={{ fontSize: "14px", color: "#64748B", margin: 0 }}>{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div style={{
      background: "rgba(15,23,42,0.5)",
      border: "1px dashed rgba(37,99,235,0.2)",
      borderRadius: "14px",
      padding: "64px 32px",
      textAlign: "center",
    }}>
      <div style={{ fontSize: "44px", marginBottom: "16px" }}>{icon}</div>
      <div style={{ fontSize: "18px", fontWeight: "600", color: "#E2E8F0", marginBottom: "8px" }}>{title}</div>
      {subtitle && <div style={{ fontSize: "14px", color: "#475569", marginBottom: "20px" }}>{subtitle}</div>}
      {action}
    </div>
  );
}

// ── LoadingSpinner ────────────────────────────────────────────────────────────
export function LoadingSpinner({ message = "Loading…" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px", gap: "12px", color: "#475569", fontSize: "14px" }}>
      <div style={{
        width: "20px", height: "20px", border: "2px solid rgba(37,99,235,0.2)",
        borderTopColor: "#2563EB", borderRadius: "50%", animation: "eq-spin 0.7s linear infinite",
        flexShrink: 0,
      }} />
      {message}
    </div>
  );
}

// ── TempPasswordBox ───────────────────────────────────────────────────────────
export function TempPasswordBox({ password, email }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      background: "rgba(234,179,8,0.08)",
      border: "1px solid rgba(234,179,8,0.3)",
      borderRadius: "10px",
      padding: "16px",
      marginTop: "4px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
        <span>⚠️</span>
        <span style={{ fontSize: "12px", fontWeight: "600", color: "#EAB308" }}>
          SAVE THIS PASSWORD — shown once only
        </span>
      </div>
      <div style={{ fontSize: "12px", color: "#94A3B8", marginBottom: "8px" }}>
        Admin email: <span style={{ color: "#E2E8F0" }}>{email}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{
          flex: 1, background: "rgba(15,23,42,0.8)", border: "1px solid rgba(234,179,8,0.2)",
          borderRadius: "6px", padding: "8px 12px",
          fontFamily: "'Courier New', monospace", fontSize: "15px",
          fontWeight: "700", color: "#EAB308", letterSpacing: "0.05em",
        }}>
          {password}
        </div>
        <Btn variant="ghost" size="sm" onClick={copy}>
          {copied ? "✓ Copied" : "Copy"}
        </Btn>
      </div>
      <div style={{ fontSize: "11px", color: "#64748B", marginTop: "8px" }}>
        User will be forced to change this password on first login.
      </div>
    </div>
  );
}
